import { deepClone } from './runtime'
import type {
  ClinicCrfCondition,
  ClinicCrfField,
  ClinicCrfFieldPresentation,
  ClinicCrfMissingCode,
  ClinicCrfPackageV1,
  ClinicCrfPresentationSection,
  ClinicCrfRule,
} from './types'

export type ClinicCrfMaskCategory =
  | 'visit'
  | 'vitals'
  | 'measurements'
  | 'history'
  | 'treatment'
  | 'safety'

export type ClinicCrfMaskApplicability = 'core' | 'common' | 'protocol'

export interface ClinicCrfMask {
  id: string
  title: string
  description: string
  category: ClinicCrfMaskCategory
  applicability: ClinicCrfMaskApplicability
  standards: string[]
  fields: ClinicCrfField[]
  rules: ClinicCrfRule[]
  sections: ClinicCrfPresentationSection[]
  presentation?: Record<string, ClinicCrfFieldPresentation>
}

export interface ClinicCrfMaskInsertResult {
  package: ClinicCrfPackageV1
  sectionIds: string[]
  fieldPaths: string[]
  ruleIds: string[]
}

interface StandardMapping {
  system: string
  code?: string
  display?: string
  domain?: string
  variable?: string
  test_code?: string
  resource?: string
  profile?: string
}

const measurementMissing: ClinicCrfMissingCode[] = ['not_done', 'unknown']
const historyMissing: ClinicCrfMissingCode[] = ['unknown', 'withheld']

function terminology(...mappings: StandardMapping[]): Record<string, unknown> {
  return { mappings }
}

function loinc(code: string, display: string): StandardMapping {
  return { system: 'http://loinc.org', code, display }
}

function ucum(code: string): StandardMapping {
  return { system: 'http://unitsofmeasure.org', code }
}

function cdisc(domain: string, variable: string, testCode?: string): StandardMapping {
  return {
    system: 'https://www.cdisc.org/standards/foundational/cdash',
    domain,
    variable,
    ...(testCode ? { test_code: testCode } : {}),
  }
}

function fhir(resource: string, profile?: string): StandardMapping {
  return {
    system: 'http://hl7.org/fhir',
    resource,
    ...(profile ? { profile } : {}),
  }
}

function isTrue(field: string): ClinicCrfCondition {
  return { field, operator: 'eq', value: true }
}

function choiceIs(field: string, value: string): ClinicCrfCondition {
  return { field, operator: 'eq', value }
}

function choiceIn(field: string, values: string[]): ClinicCrfCondition {
  return { field, operator: 'in', value: values }
}

const visitContext: ClinicCrfMask = {
  id: 'visit-context',
  title: 'Kontekst i wykonanie wizyty',
  description: 'Data, tryb, status wykonania i powód niepełnej lub niewykonanej wizyty.',
  category: 'visit',
  applicability: 'core',
  standards: ['CDISC timing', 'OpenMRS Encounter'],
  fields: [
    {
      path: 'visit.occurred_at',
      label: 'Data i czas wizyty',
      type: 'datetime',
      required: true,
      source: 'site',
      terminology: terminology(cdisc('SV', 'SVSTDTC'), fhir('Encounter')),
    },
    {
      path: 'visit.mode',
      label: 'Tryb wizyty',
      type: 'choice',
      required: true,
      options: ['in_person', 'telephone', 'video', 'home', 'other'],
      source: 'site',
      terminology: terminology(fhir('Encounter')),
    },
    {
      path: 'visit.status',
      label: 'Status wykonania wizyty',
      type: 'choice',
      required: true,
      options: ['completed', 'partial', 'not_done'],
      source: 'site',
      terminology: terminology(cdisc('SV', 'SVSTAT'), fhir('Encounter')),
    },
    {
      path: 'visit.not_done_reason',
      label: 'Powód wizyty niepełnej lub niewykonanej',
      type: 'text',
      max_length: 2000,
      source: 'site',
      visible_when: choiceIn('visit.status', ['partial', 'not_done']),
      required_when: choiceIn('visit.status', ['partial', 'not_done']),
    },
    {
      path: 'visit.comment',
      label: 'Komentarz do przebiegu wizyty',
      type: 'text',
      max_length: 4000,
      source: 'site',
    },
  ],
  rules: [],
  sections: [{
    id: 'visit_context',
    title: 'Kontekst wizyty',
    description: 'Zapisz rzeczywisty czas i sposób realizacji kontaktu zgodnie z protokołem.',
    field_paths: ['visit.occurred_at', 'visit.mode', 'visit.status', 'visit.not_done_reason', 'visit.comment'],
  }],
  presentation: {
    'visit.mode': { option_labels: { in_person: 'W ośrodku', telephone: 'Telefonicznie', video: 'Wideowizyta', home: 'W domu uczestnika', other: 'Inny' } },
    'visit.status': { option_labels: { completed: 'Wykonana w całości', partial: 'Wykonana częściowo', not_done: 'Nie wykonano' } },
    'visit.not_done_reason': { help_text: 'Nie zastępuje formularza odchylenia od protokołu, jeżeli taki formularz jest wymagany.' },
  },
}

const bloodPressure: ClinicCrfMask = {
  id: 'blood-pressure',
  title: 'Ciśnienie tętnicze',
  description: 'Ciśnienie skurczowe i rozkurczowe wraz z pozycją, stroną i metodą pomiaru.',
  category: 'vitals',
  applicability: 'common',
  standards: ['CDISC CDASH VS', 'HL7 FHIR R5', 'LOINC', 'UCUM'],
  fields: [
    {
      path: 'blood_pressure.measured_at',
      label: 'Data i czas pomiaru ciśnienia',
      type: 'datetime',
      required: true,
      source: 'site',
      terminology: terminology(cdisc('VS', 'VSDTC'), fhir('Observation', 'vitalsigns')),
    },
    {
      path: 'blood_pressure.systolic',
      label: 'Ciśnienie skurczowe',
      type: 'integer',
      required: true,
      min: 40,
      max: 300,
      unit: 'mm[Hg]',
      source: 'device',
      missing_codes: measurementMissing,
      missing_reason_required: true,
      terminology: terminology(loinc('8480-6', 'Systolic blood pressure'), ucum('mm[Hg]'), cdisc('VS', 'VSORRES', 'SYSBP'), fhir('Observation', 'bp')),
    },
    {
      path: 'blood_pressure.diastolic',
      label: 'Ciśnienie rozkurczowe',
      type: 'integer',
      required: true,
      min: 20,
      max: 200,
      unit: 'mm[Hg]',
      source: 'device',
      missing_codes: measurementMissing,
      missing_reason_required: true,
      terminology: terminology(loinc('8462-4', 'Diastolic blood pressure'), ucum('mm[Hg]'), cdisc('VS', 'VSORRES', 'DIABP'), fhir('Observation', 'bp')),
    },
    {
      path: 'blood_pressure.position',
      label: 'Pozycja podczas pomiaru',
      type: 'choice',
      required: true,
      options: ['sitting', 'standing', 'supine'],
      source: 'site',
      terminology: terminology(cdisc('VS', 'VSPOS', 'SYSBP')),
    },
    {
      path: 'blood_pressure.side',
      label: 'Strona pomiaru',
      type: 'choice',
      options: ['left', 'right', 'not_applicable'],
      source: 'site',
      terminology: terminology(cdisc('VS', 'VSLAT', 'SYSBP')),
    },
    {
      path: 'blood_pressure.method',
      label: 'Metoda pomiaru',
      type: 'choice',
      options: ['automatic', 'manual', 'other'],
      source: 'site',
      terminology: terminology(fhir('Observation', 'bp')),
    },
    {
      path: 'blood_pressure.device_id',
      label: 'Identyfikator urządzenia',
      type: 'string',
      max_length: 200,
      source: 'device',
      terminology: terminology(fhir('Device')),
    },
  ],
  rules: [{
    id: 'blood_pressure_order',
    path: 'blood_pressure.systolic',
    message: 'Ciśnienie skurczowe powinno być większe od rozkurczowego.',
    when: { all: [
      { field: 'blood_pressure.systolic', operator: 'present' },
      { field: 'blood_pressure.diastolic', operator: 'present' },
    ] },
    assert: { field: 'blood_pressure.systolic', operator: 'gt', compare_field: 'blood_pressure.diastolic' },
  }],
  sections: [{
    id: 'blood_pressure',
    title: 'Ciśnienie tętnicze',
    description: 'Warunki pomiaru muszą odpowiadać protokołowi i instrukcji urządzenia.',
    field_paths: [
      'blood_pressure.measured_at', 'blood_pressure.systolic', 'blood_pressure.diastolic',
      'blood_pressure.position', 'blood_pressure.side', 'blood_pressure.method', 'blood_pressure.device_id',
    ],
  }],
  presentation: {
    'blood_pressure.systolic': { width: 'half', help_text: 'Zakres jest szerokim zabezpieczeniem technicznym, a nie zakresem wartości prawidłowych.' },
    'blood_pressure.diastolic': { width: 'half', help_text: 'Zakres jest szerokim zabezpieczeniem technicznym, a nie zakresem wartości prawidłowych.' },
    'blood_pressure.position': { option_labels: { sitting: 'Siedząca', standing: 'Stojąca', supine: 'Leżąca' } },
    'blood_pressure.side': { option_labels: { left: 'Lewa', right: 'Prawa', not_applicable: 'Nie dotyczy' } },
    'blood_pressure.method': { option_labels: { automatic: 'Automatyczna', manual: 'Manualna', other: 'Inna' } },
  },
}

const vitalSigns: ClinicCrfMask = {
  id: 'vital-signs',
  title: 'Podstawowe parametry życiowe',
  description: 'Tętno, częstość oddechów, temperatura i saturacja z kontekstem tlenoterapii.',
  category: 'vitals',
  applicability: 'common',
  standards: ['CDISC CDASH VS', 'HL7 FHIR R5', 'LOINC', 'UCUM'],
  fields: [
    {
      path: 'vitals.measured_at',
      label: 'Data i czas pomiarów',
      type: 'datetime',
      required: true,
      source: 'site',
      terminology: terminology(cdisc('VS', 'VSDTC'), fhir('Observation', 'vitalsigns')),
    },
    {
      path: 'vitals.heart_rate',
      label: 'Tętno / częstość pracy serca',
      type: 'integer',
      min: 10,
      max: 350,
      unit: '/min',
      source: 'device',
      missing_codes: measurementMissing,
      terminology: terminology(loinc('8867-4', 'Heart rate'), ucum('/min'), cdisc('VS', 'VSORRES', 'PULSE'), fhir('Observation', 'heartrate')),
    },
    {
      path: 'vitals.respiratory_rate',
      label: 'Częstość oddechów',
      type: 'integer',
      min: 1,
      max: 120,
      unit: '/min',
      source: 'site',
      missing_codes: measurementMissing,
      terminology: terminology(loinc('9279-1', 'Respiratory rate'), ucum('/min'), cdisc('VS', 'VSORRES', 'RESP'), fhir('Observation', 'resprate')),
    },
    {
      path: 'vitals.temperature',
      label: 'Temperatura ciała',
      type: 'number',
      precision: 1,
      min: 25,
      max: 45,
      unit: 'Cel',
      source: 'device',
      missing_codes: measurementMissing,
      terminology: terminology(loinc('8310-5', 'Body temperature'), ucum('Cel'), cdisc('VS', 'VSORRES', 'TEMP'), fhir('Observation', 'bodytemp')),
    },
    {
      path: 'vitals.temperature_site',
      label: 'Miejsce pomiaru temperatury',
      type: 'choice',
      options: ['oral', 'axillary', 'tympanic', 'temporal', 'rectal', 'other'],
      source: 'site',
      terminology: terminology(loinc('8327-9', 'Body temperature measurement site'), fhir('Observation', 'bodytemp')),
    },
    {
      path: 'vitals.oxygen_saturation',
      label: 'Saturacja krwi obwodowej (SpO₂)',
      type: 'number',
      precision: 1,
      min: 0,
      max: 100,
      unit: '%',
      source: 'device',
      missing_codes: measurementMissing,
      terminology: terminology(
        loinc('2708-6', 'Oxygen saturation in arterial blood'),
        loinc('59408-5', 'Oxygen saturation by pulse oximetry'),
        ucum('%'),
        fhir('Observation', 'oxygensat'),
      ),
    },
    {
      path: 'vitals.oxygen_support',
      label: 'Czy podczas pomiaru stosowano tlenoterapię?',
      type: 'boolean',
      required: true,
      source: 'site',
      missing_codes: ['unknown'],
      terminology: terminology(fhir('Observation', 'oxygensat')),
    },
    {
      path: 'vitals.oxygen_delivery',
      label: 'Sposób podaży tlenu',
      type: 'choice',
      options: ['nasal_cannula', 'face_mask', 'high_flow', 'ventilation', 'other'],
      source: 'site',
      visible_when: isTrue('vitals.oxygen_support'),
      required_when: isTrue('vitals.oxygen_support'),
    },
    {
      path: 'vitals.oxygen_flow',
      label: 'Przepływ tlenu',
      type: 'number',
      precision: 1,
      min: 0,
      max: 100,
      unit: 'L/min',
      source: 'device',
      visible_when: isTrue('vitals.oxygen_support'),
      terminology: terminology(ucum('L/min'), fhir('Observation', 'oxygensat')),
    },
  ],
  rules: [],
  sections: [{
    id: 'vital_signs',
    title: 'Podstawowe parametry życiowe',
    description: 'Dostosuj wymagane pomiary i warunki wykonania do protokołu oraz populacji.',
    field_paths: [
      'vitals.measured_at', 'vitals.heart_rate', 'vitals.respiratory_rate', 'vitals.temperature',
      'vitals.temperature_site', 'vitals.oxygen_saturation', 'vitals.oxygen_support',
      'vitals.oxygen_delivery', 'vitals.oxygen_flow',
    ],
  }],
  presentation: {
    'vitals.heart_rate': { width: 'half', help_text: 'Zakres techniczny nie definiuje normy klinicznej.' },
    'vitals.respiratory_rate': { width: 'half', help_text: 'Zakres techniczny nie definiuje normy klinicznej.' },
    'vitals.temperature': { width: 'half' },
    'vitals.temperature_site': { width: 'half', option_labels: { oral: 'Jama ustna', axillary: 'Dół pachowy', tympanic: 'Ucho', temporal: 'Tętnica skroniowa / czoło', rectal: 'Odbytniczo', other: 'Inne' } },
    'vitals.oxygen_saturation': { width: 'half', help_text: 'Zawsze zapisuj kontekst tlenoterapii; próg alarmowy wynika z protokołu, nie z tej maski.' },
    'vitals.oxygen_support': { width: 'half' },
    'vitals.oxygen_delivery': { option_labels: { nasal_cannula: 'Wąsy tlenowe', face_mask: 'Maska twarzowa', high_flow: 'Wysokoprzepływowa tlenoterapia', ventilation: 'Wentylacja', other: 'Inny sposób' } },
  },
}

const anthropometry: ClinicCrfMask = {
  id: 'anthropometry',
  title: 'Antropometria',
  description: 'Wzrost, masa ciała i BMI ze standardowymi kodami LOINC i jednostkami UCUM.',
  category: 'measurements',
  applicability: 'common',
  standards: ['CDISC CDASH VS', 'HL7 FHIR R5', 'LOINC', 'UCUM'],
  fields: [
    {
      path: 'anthropometry.measured_at', label: 'Data i czas pomiaru', type: 'datetime', required: true, source: 'site',
      terminology: terminology(cdisc('VS', 'VSDTC'), fhir('Observation', 'vitalsigns')),
    },
    {
      path: 'anthropometry.height', label: 'Wzrost', type: 'number', precision: 1, min: 0, max: 300, unit: 'cm', source: 'device', missing_codes: measurementMissing,
      terminology: terminology(loinc('8302-2', 'Body height'), ucum('cm'), cdisc('VS', 'VSORRES', 'HEIGHT'), fhir('Observation', 'bodyheight')),
    },
    {
      path: 'anthropometry.weight', label: 'Masa ciała', type: 'number', precision: 2, min: 0, max: 1000, unit: 'kg', source: 'device', missing_codes: measurementMissing,
      terminology: terminology(loinc('29463-7', 'Body weight'), ucum('kg'), cdisc('VS', 'VSORRES', 'WEIGHT'), fhir('Observation', 'bodyweight')),
    },
    {
      path: 'anthropometry.bmi', label: 'Wskaźnik masy ciała (BMI)', type: 'number', precision: 1, min: 0, max: 300, unit: 'kg/m2', source: 'derived', missing_codes: ['not_done'],
      terminology: terminology(loinc('39156-5', 'Body mass index'), ucum('kg/m2'), fhir('Observation', 'bmi')),
    },
  ],
  rules: [],
  sections: [{ id: 'anthropometry', title: 'Antropometria', description: 'Zachowaj wartości źródłowe; BMI może być wyliczane w systemie docelowym.', field_paths: ['anthropometry.measured_at', 'anthropometry.height', 'anthropometry.weight', 'anthropometry.bmi'] }],
  presentation: {
    'anthropometry.height': { width: 'half', help_text: 'Zakres jest zabezpieczeniem technicznym; dostosuj go do populacji badania.' },
    'anthropometry.weight': { width: 'half', help_text: 'Zakres jest zabezpieczeniem technicznym; dostosuj go do populacji badania.' },
    'anthropometry.bmi': { help_text: 'W produkcyjnym eCRF warto wyliczać BMI z zapisanych wartości wzrostu i masy oraz zachować regułę pochodzenia.' },
  },
}

const demographics: ClinicCrfMask = {
  id: 'minimal-demographics',
  title: 'Minimalna demografia kliniczna',
  description: 'Wiek, jednostka wieku, niepełna data urodzenia i płeć przy urodzeniu z naciskiem na minimalizację danych.',
  category: 'history',
  applicability: 'common',
  standards: ['CDISC CDASH DM', 'Privacy by design'],
  fields: [
    {
      path: 'demographics.age', label: 'Wiek w dniu oceny', type: 'integer', min: 0, max: 150, source: 'site', missing_codes: historyMissing,
      terminology: terminology(cdisc('DM', 'AGE')),
    },
    {
      path: 'demographics.age_unit', label: 'Jednostka wieku', type: 'choice', options: ['days', 'weeks', 'months', 'years'], source: 'site',
      visible_when: { field: 'demographics.age', operator: 'present' }, required_when: { field: 'demographics.age', operator: 'present' },
      terminology: terminology(cdisc('DM', 'AGEU')),
    },
    {
      path: 'demographics.birth_date', label: 'Data urodzenia (jeżeli wymaga protokół)', type: 'partial_date', source: 'subject', missing_codes: historyMissing,
      terminology: terminology(cdisc('DM', 'BRTHDTC'), fhir('Patient')),
    },
    {
      path: 'demographics.sex_at_birth', label: 'Płeć przy urodzeniu', type: 'choice', options: ['female', 'male', 'unknown', 'undifferentiated'], source: 'subject', missing_codes: historyMissing,
      terminology: terminology(cdisc('DM', 'SEX'), fhir('Patient')),
    },
  ],
  rules: [],
  sections: [{ id: 'minimal_demographics', title: 'Dane demograficzne', description: 'Zbieraj tylko pola uzasadnione protokołem i podstawą prawną.', field_paths: ['demographics.age', 'demographics.age_unit', 'demographics.birth_date', 'demographics.sex_at_birth'] }],
  presentation: {
    'demographics.age_unit': { option_labels: { days: 'Dni', weeks: 'Tygodnie', months: 'Miesiące', years: 'Lata' } },
    'demographics.birth_date': { help_text: 'Pełna data urodzenia zwiększa ryzyko identyfikacji. Jeśli wystarczy wiek lub rok, usuń niepotrzebną dokładność.' },
    'demographics.sex_at_birth': { option_labels: { female: 'Żeńska', male: 'Męska', unknown: 'Nieznana', undifferentiated: 'Niezróżnicowana' } },
  },
}

const currentSymptoms: ClinicCrfMask = {
  id: 'current-symptoms',
  title: 'Aktualne objawy',
  description: 'Pojedynczy objaw z początkiem, nasileniem, przebiegiem i statusem trwania.',
  category: 'history',
  applicability: 'protocol',
  standards: ['CDISC CDASH CE/MH'],
  fields: [
    { path: 'symptom.present', label: 'Czy występują obecnie objawy?', type: 'boolean', required: true, source: 'subject', missing_codes: ['unknown', 'withheld'] },
    { path: 'symptom.term', label: 'Objaw', type: 'string', max_length: 500, source: 'subject', visible_when: isTrue('symptom.present'), required_when: isTrue('symptom.present'), terminology: terminology(cdisc('CE', 'CETERM')) },
    { path: 'symptom.onset', label: 'Data początku objawu', type: 'partial_date', source: 'subject', visible_when: isTrue('symptom.present'), missing_codes: historyMissing, terminology: terminology(cdisc('CE', 'CESTDTC')) },
    { path: 'symptom.severity', label: 'Nasilenie objawu', type: 'choice', options: ['mild', 'moderate', 'severe'], source: 'subject', visible_when: isTrue('symptom.present'), terminology: terminology(cdisc('CE', 'CESEV')) },
    { path: 'symptom.ongoing', label: 'Czy objaw nadal trwa?', type: 'boolean', source: 'subject', visible_when: isTrue('symptom.present'), missing_codes: ['unknown'] },
    { path: 'symptom.course', label: 'Przebieg objawu', type: 'choice', options: ['improving', 'stable', 'worsening', 'fluctuating', 'unknown'], source: 'subject', visible_when: isTrue('symptom.present') },
    { path: 'symptom.comment', label: 'Dodatkowy opis objawu', type: 'text', max_length: 4000, source: 'subject', visible_when: isTrue('symptom.present') },
  ],
  rules: [],
  sections: [{ id: 'current_symptom', title: 'Aktualny objaw', description: 'Maska opisuje jeden objaw. Dodaj kolejną kopię dla następnego wpisu lub użyj grupy powtarzalnej w systemie docelowym.', field_paths: ['symptom.present', 'symptom.term', 'symptom.onset', 'symptom.severity', 'symptom.ongoing', 'symptom.course', 'symptom.comment'] }],
  presentation: {
    'symptom.severity': { option_labels: { mild: 'Łagodne', moderate: 'Umiarkowane', severe: 'Ciężkie' } },
    'symptom.course': { option_labels: { improving: 'Poprawa', stable: 'Bez zmian', worsening: 'Pogorszenie', fluctuating: 'Zmienny', unknown: 'Nieznany' } },
  },
}

const medicalHistory: ClinicCrfMask = {
  id: 'medical-history-entry',
  title: 'Wywiad chorobowy — wpis',
  description: 'Pojedyncza choroba lub zdarzenie w wywiadzie: termin, daty, trwanie, status i znaczenie.',
  category: 'history',
  applicability: 'common',
  standards: ['CDISC CDASH MH'],
  fields: [
    { path: 'medical_history.any', label: 'Czy w wywiadzie występują istotne choroby lub zdarzenia?', type: 'boolean', required: true, source: 'subject', missing_codes: ['unknown', 'withheld'], terminology: terminology(cdisc('MH', 'MHYN')) },
    { path: 'medical_history.term', label: 'Choroba lub zdarzenie medyczne', type: 'string', max_length: 500, source: 'site', visible_when: isTrue('medical_history.any'), required_when: isTrue('medical_history.any'), terminology: terminology(cdisc('MH', 'MHTERM')) },
    { path: 'medical_history.start_date', label: 'Data początku', type: 'partial_date', source: 'subject', visible_when: isTrue('medical_history.any'), missing_codes: historyMissing, terminology: terminology(cdisc('MH', 'MHSTDTC')) },
    { path: 'medical_history.ongoing', label: 'Czy stan nadal trwa?', type: 'boolean', source: 'site', visible_when: isTrue('medical_history.any'), missing_codes: ['unknown'], terminology: terminology(cdisc('MH', 'MHENRF')) },
    { path: 'medical_history.end_date', label: 'Data zakończenia', type: 'partial_date', source: 'subject', visible_when: { all: [isTrue('medical_history.any'), { field: 'medical_history.ongoing', operator: 'eq', value: false }] }, missing_codes: historyMissing, terminology: terminology(cdisc('MH', 'MHENDTC')) },
    { path: 'medical_history.status', label: 'Aktualny status', type: 'choice', options: ['active', 'controlled', 'resolved', 'unknown'], source: 'site', visible_when: isTrue('medical_history.any') },
    { path: 'medical_history.relevant', label: 'Istotne dla bieżącego badania', type: 'boolean', source: 'site', visible_when: isTrue('medical_history.any') },
    { path: 'medical_history.comment', label: 'Komentarz do wywiadu', type: 'text', max_length: 4000, source: 'site', visible_when: isTrue('medical_history.any') },
  ],
  rules: [],
  sections: [{ id: 'medical_history_entry', title: 'Wywiad chorobowy', description: 'Zapisuj jeden rozpoznany stan, znak lub objaw na wpis; zakres istotności definiuje protokół.', field_paths: ['medical_history.any', 'medical_history.term', 'medical_history.start_date', 'medical_history.ongoing', 'medical_history.end_date', 'medical_history.status', 'medical_history.relevant', 'medical_history.comment'] }],
  presentation: {
    'medical_history.term': { help_text: 'Jedno rozpoznanie, znak lub objaw na wpis. Kodowanie terminologiczne wykonuje kontrolowany proces systemu docelowego.' },
    'medical_history.status': { option_labels: { active: 'Aktywny', controlled: 'Kontrolowany', resolved: 'Zakończony / ustąpił', unknown: 'Nieznany' } },
  },
}

const allergies: ClinicCrfMask = {
  id: 'allergy-intolerance',
  title: 'Alergie i nietolerancje',
  description: 'Jawny status „brak znanych / występują / nieznane” oraz pojedyncza reakcja.',
  category: 'history',
  applicability: 'common',
  standards: ['HL7 FHIR R5 AllergyIntolerance'],
  fields: [
    { path: 'allergy.known_status', label: 'Status znanych alergii lub nietolerancji', type: 'choice', required: true, options: ['none_known', 'known', 'unknown'], source: 'subject', terminology: terminology(fhir('AllergyIntolerance')) },
    { path: 'allergy.substance', label: 'Substancja / czynnik', type: 'string', max_length: 500, source: 'site', visible_when: choiceIs('allergy.known_status', 'known'), required_when: choiceIs('allergy.known_status', 'known'), terminology: terminology(fhir('AllergyIntolerance')) },
    { path: 'allergy.reaction', label: 'Opis reakcji', type: 'text', max_length: 2000, source: 'subject', visible_when: choiceIs('allergy.known_status', 'known'), required_when: choiceIs('allergy.known_status', 'known'), terminology: terminology(fhir('AllergyIntolerance')) },
    { path: 'allergy.onset', label: 'Początek reakcji / rozpoznania', type: 'partial_date', source: 'subject', visible_when: choiceIs('allergy.known_status', 'known'), missing_codes: historyMissing },
    { path: 'allergy.severity', label: 'Nasilenie najcięższej reakcji', type: 'choice', options: ['mild', 'moderate', 'severe', 'unknown'], source: 'site', visible_when: choiceIs('allergy.known_status', 'known'), terminology: terminology(fhir('AllergyIntolerance')) },
    { path: 'allergy.clinical_status', label: 'Status kliniczny', type: 'choice', options: ['active', 'inactive', 'resolved', 'unknown'], source: 'site', visible_when: choiceIs('allergy.known_status', 'known'), terminology: terminology(fhir('AllergyIntolerance')) },
  ],
  rules: [],
  sections: [{ id: 'allergies', title: 'Alergie i nietolerancje', description: 'Maska rozróżnia potwierdzony brak znanych alergii od braku informacji.', field_paths: ['allergy.known_status', 'allergy.substance', 'allergy.reaction', 'allergy.onset', 'allergy.severity', 'allergy.clinical_status'] }],
  presentation: {
    'allergy.known_status': { option_labels: { none_known: 'Brak znanych alergii lub nietolerancji', known: 'Występują', unknown: 'Nie wiadomo' } },
    'allergy.severity': { option_labels: { mild: 'Łagodne', moderate: 'Umiarkowane', severe: 'Ciężkie', unknown: 'Nieznane' } },
    'allergy.clinical_status': { option_labels: { active: 'Aktywna', inactive: 'Nieaktywna', resolved: 'Ustąpiła', unknown: 'Nieznany' } },
  },
}

const concomitantMedication: ClinicCrfMask = {
  id: 'concomitant-medication',
  title: 'Lek towarzyszący — wpis',
  description: 'Nazwa leku, wskazanie, dawka, jednostka, postać, droga, częstość i daty stosowania.',
  category: 'treatment',
  applicability: 'common',
  standards: ['CDISC CDASH CM', 'HL7 FHIR R5 MedicationStatement'],
  fields: [
    { path: 'medication.any', label: 'Czy stosowano leki towarzyszące?', type: 'boolean', required: true, source: 'subject', missing_codes: ['unknown', 'withheld'], terminology: terminology(cdisc('CM', 'CMYN'), fhir('MedicationStatement')) },
    { path: 'medication.name', label: 'Nazwa leku lub terapii (verbatim)', type: 'string', max_length: 500, source: 'subject', visible_when: isTrue('medication.any'), required_when: isTrue('medication.any'), terminology: terminology(cdisc('CM', 'CMTRT'), fhir('MedicationStatement')) },
    { path: 'medication.indication', label: 'Wskazanie', type: 'string', max_length: 500, source: 'site', visible_when: isTrue('medication.any'), terminology: terminology(cdisc('CM', 'CMINDC'), fhir('MedicationStatement')) },
    { path: 'medication.dose_amount', label: 'Dawka na podanie', type: 'number', precision: 3, min: 0, source: 'subject', visible_when: isTrue('medication.any'), terminology: terminology(cdisc('CM', 'CMDOSE'), fhir('MedicationStatement')) },
    { path: 'medication.dose_unit', label: 'Jednostka dawki', type: 'string', max_length: 80, source: 'subject', visible_when: { field: 'medication.dose_amount', operator: 'present' }, required_when: { field: 'medication.dose_amount', operator: 'present' }, terminology: terminology(cdisc('CM', 'CMDOSU'), fhir('MedicationStatement')) },
    { path: 'medication.dose_form', label: 'Postać leku', type: 'string', max_length: 200, source: 'subject', visible_when: isTrue('medication.any'), terminology: terminology(cdisc('CM', 'CMDOSFRM'), fhir('MedicationStatement')) },
    { path: 'medication.route', label: 'Droga podania', type: 'string', max_length: 200, source: 'subject', visible_when: isTrue('medication.any'), terminology: terminology(cdisc('CM', 'CMROUTE'), fhir('MedicationStatement')) },
    { path: 'medication.frequency', label: 'Częstość stosowania', type: 'string', max_length: 200, source: 'subject', visible_when: isTrue('medication.any'), terminology: terminology(cdisc('CM', 'CMDOSFRQ'), fhir('MedicationStatement')) },
    { path: 'medication.start_date', label: 'Data rozpoczęcia', type: 'partial_date', source: 'subject', visible_when: isTrue('medication.any'), missing_codes: historyMissing, terminology: terminology(cdisc('CM', 'CMSTDTC'), fhir('MedicationStatement')) },
    { path: 'medication.ongoing', label: 'Czy stosowanie trwa?', type: 'boolean', source: 'subject', visible_when: isTrue('medication.any'), missing_codes: ['unknown'], terminology: terminology(cdisc('CM', 'CMENRF'), fhir('MedicationStatement')) },
    { path: 'medication.end_date', label: 'Data zakończenia', type: 'partial_date', source: 'subject', visible_when: { all: [isTrue('medication.any'), { field: 'medication.ongoing', operator: 'eq', value: false }] }, missing_codes: historyMissing, terminology: terminology(cdisc('CM', 'CMENDTC'), fhir('MedicationStatement')) },
  ],
  rules: [],
  sections: [{ id: 'concomitant_medication', title: 'Lek towarzyszący', description: 'Maska opisuje jeden lek lub terapię. W systemie docelowym użyj grupy powtarzalnej.', field_paths: ['medication.any', 'medication.name', 'medication.indication', 'medication.dose_amount', 'medication.dose_unit', 'medication.dose_form', 'medication.route', 'medication.frequency', 'medication.start_date', 'medication.ongoing', 'medication.end_date'] }],
  presentation: {
    'medication.name': { help_text: 'Zapisz nazwę podaną przez uczestnika. Kodowanie WHO Drug lub innym słownikiem powinno odbywać się poza polem verbatim i zgodnie z licencją.' },
    'medication.indication': { help_text: 'Jeżeli istnieje rozpoznanie, podaj rozpoznanie; w przeciwnym razie właściwy znak lub objaw.' },
  },
}

const substanceUse: ClinicCrfMask = {
  id: 'substance-use',
  title: 'Używanie substancji — wpis',
  description: 'Status, rodzaj, ilość, jednostka, częstość oraz okres używania substancji.',
  category: 'history',
  applicability: 'protocol',
  standards: ['CDISC CDASH SU'],
  fields: [
    { path: 'substance.status', label: 'Status używania substancji', type: 'choice', required: true, options: ['never', 'former', 'current', 'unknown'], source: 'subject', terminology: terminology(cdisc('SU', 'SUSTAT')) },
    { path: 'substance.category', label: 'Kategoria substancji', type: 'choice', options: ['tobacco', 'alcohol', 'recreational_drug', 'other'], source: 'subject', visible_when: choiceIn('substance.status', ['former', 'current']), required_when: choiceIn('substance.status', ['former', 'current']), terminology: terminology(cdisc('SU', 'SUCAT')) },
    { path: 'substance.name', label: 'Substancja / produkt', type: 'string', max_length: 500, source: 'subject', visible_when: choiceIn('substance.status', ['former', 'current']), terminology: terminology(cdisc('SU', 'SUTRT')) },
    { path: 'substance.amount', label: 'Typowa ilość', type: 'number', precision: 2, min: 0, source: 'subject', visible_when: choiceIn('substance.status', ['former', 'current']), terminology: terminology(cdisc('SU', 'SUDOSTOT')) },
    { path: 'substance.amount_unit', label: 'Jednostka ilości', type: 'string', max_length: 100, source: 'subject', visible_when: { field: 'substance.amount', operator: 'present' }, required_when: { field: 'substance.amount', operator: 'present' }, terminology: terminology(cdisc('SU', 'SUDOSU')) },
    { path: 'substance.frequency', label: 'Częstość używania', type: 'string', max_length: 200, source: 'subject', visible_when: choiceIn('substance.status', ['former', 'current']), terminology: terminology(cdisc('SU', 'SUDOSFRQ')) },
    { path: 'substance.start_date', label: 'Data rozpoczęcia', type: 'partial_date', source: 'subject', visible_when: choiceIn('substance.status', ['former', 'current']), missing_codes: historyMissing, terminology: terminology(cdisc('SU', 'SUSTDTC')) },
    { path: 'substance.end_date', label: 'Data zakończenia', type: 'partial_date', source: 'subject', visible_when: choiceIs('substance.status', 'former'), missing_codes: historyMissing, terminology: terminology(cdisc('SU', 'SUENDTC')) },
    { path: 'substance.comment', label: 'Komentarz', type: 'text', max_length: 2000, source: 'subject', visible_when: choiceIn('substance.status', ['former', 'current']) },
  ],
  rules: [],
  sections: [{ id: 'substance_use', title: 'Używanie substancji', description: 'Zbieraj wyłącznie dane potrzebne do celu badania i z użyciem protokołowej definicji porcji/jednostki.', field_paths: ['substance.status', 'substance.category', 'substance.name', 'substance.amount', 'substance.amount_unit', 'substance.frequency', 'substance.start_date', 'substance.end_date', 'substance.comment'] }],
  presentation: {
    'substance.status': { option_labels: { never: 'Nigdy', former: 'W przeszłości', current: 'Obecnie', unknown: 'Nie wiadomo' } },
    'substance.category': { option_labels: { tobacco: 'Tytoń / nikotyna', alcohol: 'Alkohol', recreational_drug: 'Substancja rekreacyjna', other: 'Inna' } },
    'substance.amount': { help_text: 'Nie zakładaj znaczenia „standardowej porcji”; protokół musi definiować ilość i jednostkę.' },
  },
}

const physicalExam: ClinicCrfMask = {
  id: 'physical-exam-confirmation',
  title: 'Badanie fizykalne — potwierdzenie',
  description: 'Fakt wykonania, czas, wynik ogólny i skierowanie nieprawidłowego ustalenia do MH albo AE.',
  category: 'safety',
  applicability: 'protocol',
  standards: ['CDISC CDASH PE best practice'],
  fields: [
    { path: 'physical_exam.performed', label: 'Czy wykonano badanie fizykalne?', type: 'boolean', required: true, source: 'site', missing_codes: ['unknown'], terminology: terminology(cdisc('PR', 'PROCCUR')) },
    { path: 'physical_exam.performed_at', label: 'Data i czas badania fizykalnego', type: 'datetime', source: 'site', visible_when: isTrue('physical_exam.performed'), required_when: isTrue('physical_exam.performed'), terminology: terminology(cdisc('PR', 'PRSTDTC')) },
    { path: 'physical_exam.overall_result', label: 'Wynik ogólny badania', type: 'choice', options: ['normal', 'abnormal', 'not_evaluable'], source: 'site', visible_when: isTrue('physical_exam.performed') },
    { path: 'physical_exam.new_finding', label: 'Czy stwierdzono nowe lub pogorszone odchylenie?', type: 'boolean', source: 'site', visible_when: choiceIs('physical_exam.overall_result', 'abnormal'), required_when: choiceIs('physical_exam.overall_result', 'abnormal') },
    { path: 'physical_exam.finding_route', label: 'Gdzie udokumentowano odchylenie?', type: 'choice', options: ['medical_history', 'adverse_event', 'other'], source: 'site', visible_when: isTrue('physical_exam.new_finding'), required_when: isTrue('physical_exam.new_finding') },
    { path: 'physical_exam.comment', label: 'Komentarz do wykonania badania', type: 'text', max_length: 2000, source: 'site' },
  ],
  rules: [],
  sections: [{ id: 'physical_exam', title: 'Badanie fizykalne', description: 'Maska rejestruje wykonanie. Nieprawidłowości powinny trafić do odpowiedniego formularza wywiadu (MH) lub zdarzenia niepożądanego (AE).', field_paths: ['physical_exam.performed', 'physical_exam.performed_at', 'physical_exam.overall_result', 'physical_exam.new_finding', 'physical_exam.finding_route', 'physical_exam.comment'] }],
  presentation: {
    'physical_exam.overall_result': { option_labels: { normal: 'Bez odchyleń', abnormal: 'Nieprawidłowy', not_evaluable: 'Nie można ocenić' } },
    'physical_exam.finding_route': { option_labels: { medical_history: 'Wywiad chorobowy (MH)', adverse_event: 'Zdarzenie niepożądane (AE)', other: 'Inne miejsce' } },
  },
}

const adverseEvent: ClinicCrfMask = {
  id: 'adverse-event-entry',
  title: 'Zdarzenie niepożądane — wpis',
  description: 'Termin, daty, nasilenie, serious, kryterium ciężkości, związek, działanie i wynik.',
  category: 'safety',
  applicability: 'common',
  standards: ['CDISC CDASH AE'],
  fields: [
    { path: 'adverse_event.occurred', label: 'Czy wystąpiło zdarzenie niepożądane?', type: 'boolean', required: true, source: 'site', missing_codes: ['unknown'], terminology: terminology(cdisc('AE', 'AEYN'), fhir('AdverseEvent')) },
    { path: 'adverse_event.term', label: 'Nazwa zdarzenia niepożądanego', type: 'string', max_length: 500, source: 'site', visible_when: isTrue('adverse_event.occurred'), required_when: isTrue('adverse_event.occurred'), terminology: terminology(cdisc('AE', 'AETERM'), fhir('AdverseEvent')) },
    { path: 'adverse_event.onset', label: 'Data początku', type: 'partial_date', source: 'site', visible_when: isTrue('adverse_event.occurred'), required_when: isTrue('adverse_event.occurred'), missing_codes: ['unknown'], terminology: terminology(cdisc('AE', 'AESTDTC'), fhir('AdverseEvent')) },
    { path: 'adverse_event.ongoing', label: 'Czy zdarzenie nadal trwa?', type: 'boolean', source: 'site', visible_when: isTrue('adverse_event.occurred'), missing_codes: ['unknown'], terminology: terminology(cdisc('AE', 'AEENRF')) },
    { path: 'adverse_event.end_date', label: 'Data zakończenia', type: 'partial_date', source: 'site', visible_when: { all: [isTrue('adverse_event.occurred'), { field: 'adverse_event.ongoing', operator: 'eq', value: false }] }, missing_codes: ['unknown'], terminology: terminology(cdisc('AE', 'AEENDTC')) },
    { path: 'adverse_event.severity', label: 'Nasilenie zdarzenia', type: 'choice', options: ['mild', 'moderate', 'severe'], source: 'site', visible_when: isTrue('adverse_event.occurred'), required_when: isTrue('adverse_event.occurred'), terminology: terminology(cdisc('AE', 'AESEV'), fhir('AdverseEvent')) },
    { path: 'adverse_event.serious', label: 'Czy zdarzenie jest poważne (serious)?', type: 'boolean', source: 'site', visible_when: isTrue('adverse_event.occurred'), required_when: isTrue('adverse_event.occurred'), terminology: terminology(cdisc('AE', 'AESER'), fhir('AdverseEvent')) },
    { path: 'adverse_event.seriousness_reason', label: 'Kryterium powagi zdarzenia', type: 'multichoice', options: ['death', 'life_threatening', 'hospitalisation', 'disability', 'congenital_anomaly', 'other_medically_important'], source: 'site', visible_when: isTrue('adverse_event.serious'), required_when: isTrue('adverse_event.serious'), terminology: terminology(cdisc('AE', 'AESDTH/AESLIFE/AESHOSP/AESDISAB/AESCONG/AESMIE')) },
    { path: 'adverse_event.relationship', label: 'Związek z interwencją badaną', type: 'choice', options: ['not_related', 'unlikely', 'possible', 'probable', 'definite', 'not_assessed'], source: 'site', visible_when: isTrue('adverse_event.occurred'), terminology: terminology(cdisc('AE', 'AEREL'), fhir('AdverseEvent')) },
    { path: 'adverse_event.action_taken', label: 'Działanie dotyczące interwencji badanej', type: 'choice', options: ['none', 'dose_not_changed', 'dose_reduced', 'dose_increased', 'interrupted', 'withdrawn', 'not_applicable', 'other'], source: 'site', visible_when: isTrue('adverse_event.occurred'), terminology: terminology(cdisc('AE', 'AEACN'), fhir('AdverseEvent')) },
    { path: 'adverse_event.outcome', label: 'Wynik zdarzenia', type: 'choice', options: ['recovered', 'recovering', 'not_recovered', 'recovered_with_sequelae', 'fatal', 'unknown'], source: 'site', visible_when: isTrue('adverse_event.occurred'), terminology: terminology(cdisc('AE', 'AEOUT'), fhir('AdverseEvent')) },
  ],
  rules: [{
    id: 'fatal_event_is_serious',
    path: 'adverse_event.serious',
    message: 'Zdarzenie ze skutkiem śmiertelnym musi być oznaczone jako serious.',
    when: choiceIs('adverse_event.outcome', 'fatal'),
    assert: isTrue('adverse_event.serious'),
  }],
  sections: [{ id: 'adverse_event_entry', title: 'Zdarzenie niepożądane', description: 'Maska opisuje jeden wpis. Nasilenie i powaga (seriousness) są odrębnymi pojęciami.', field_paths: ['adverse_event.occurred', 'adverse_event.term', 'adverse_event.onset', 'adverse_event.ongoing', 'adverse_event.end_date', 'adverse_event.severity', 'adverse_event.serious', 'adverse_event.seriousness_reason', 'adverse_event.relationship', 'adverse_event.action_taken', 'adverse_event.outcome'] }],
  presentation: {
    'adverse_event.term': { help_text: 'Jedno rozpoznanie, znak lub objaw na wpis. Maska nie zawiera słownika MedDRA; kodowanie wymaga właściwej licencji i wersji.' },
    'adverse_event.severity': { option_labels: { mild: 'Łagodne', moderate: 'Umiarkowane', severe: 'Ciężkie nasilenie' }, help_text: 'Nasilenie nie jest tym samym co klasyfikacja serious.' },
    'adverse_event.seriousness_reason': { option_labels: { death: 'Zgon', life_threatening: 'Zagrożenie życia', hospitalisation: 'Hospitalizacja lub jej przedłużenie', disability: 'Trwała lub znaczna niepełnosprawność', congenital_anomaly: 'Wada wrodzona', other_medically_important: 'Inne ważne medycznie zdarzenie' } },
    'adverse_event.relationship': { option_labels: { not_related: 'Brak związku', unlikely: 'Mało prawdopodobny', possible: 'Możliwy', probable: 'Prawdopodobny', definite: 'Pewny', not_assessed: 'Nie oceniono' } },
    'adverse_event.action_taken': { option_labels: { none: 'Brak działania', dose_not_changed: 'Dawka bez zmian', dose_reduced: 'Zmniejszono dawkę', dose_increased: 'Zwiększono dawkę', interrupted: 'Przerwano czasowo', withdrawn: 'Odstawiono', not_applicable: 'Nie dotyczy', other: 'Inne' } },
    'adverse_event.outcome': { option_labels: { recovered: 'Ustąpiło', recovering: 'Ustępuje', not_recovered: 'Nie ustąpiło', recovered_with_sequelae: 'Ustąpiło z następstwami', fatal: 'Zgon', unknown: 'Nieznany' } },
  },
}

export const CLINIC_CRF_MASKS: ClinicCrfMask[] = [
  visitContext,
  bloodPressure,
  vitalSigns,
  anthropometry,
  demographics,
  currentSymptoms,
  medicalHistory,
  allergies,
  concomitantMedication,
  substanceUse,
  physicalExam,
  adverseEvent,
]

export function clinicalMaskById(id: string): ClinicCrfMask | null {
  const mask = CLINIC_CRF_MASKS.find((candidate) => candidate.id === id)
  return mask ? deepClone(mask) : null
}

function pathConflicts(candidate: string, paths: Set<string>): boolean {
  return [...paths].some((path) => (
    path === candidate
    || path.startsWith(`${candidate}.`)
    || candidate.startsWith(`${path}.`)
  ))
}

function uniquePath(base: string, paths: Set<string>): string {
  if (!pathConflicts(base, paths)) return base
  const parts = base.split('.')
  const leaf = parts.pop() ?? 'field'
  for (let index = 2; index < 10_000; index += 1) {
    const candidate = [...parts, `${leaf}_${index}`].join('.')
    if (!pathConflicts(candidate, paths)) return candidate
  }
  throw new Error(`Nie udało się utworzyć unikalnej ścieżki dla ${base}.`)
}

function uniqueId(base: string, ids: Set<string>): string {
  if (!ids.has(base)) return base
  for (let index = 2; index < 10_000; index += 1) {
    const candidate = `${base}_${index}`
    if (!ids.has(candidate)) return candidate
  }
  throw new Error(`Nie udało się utworzyć unikalnego identyfikatora dla ${base}.`)
}

function remapCondition(condition: ClinicCrfCondition, paths: Map<string, string>): ClinicCrfCondition {
  if ('all' in condition) return { all: condition.all.map((entry) => remapCondition(entry, paths)) }
  if ('any' in condition) return { any: condition.any.map((entry) => remapCondition(entry, paths)) }
  if ('not' in condition) return { not: remapCondition(condition.not, paths) }
  return {
    ...condition,
    field: paths.get(condition.field) ?? condition.field,
    ...(condition.compare_field
      ? { compare_field: paths.get(condition.compare_field) ?? condition.compare_field }
      : {}),
  }
}

export function packageWithClinicalMask(
  source: ClinicCrfPackageV1,
  maskSource: ClinicCrfMask,
): ClinicCrfMaskInsertResult {
  if (source.definition.fields.length + maskSource.fields.length > 500) {
    throw new Error('Dodanie maski przekroczyłoby limit 500 pól kontraktu.')
  }

  const output = deepClone(source)
  const mask = deepClone(maskSource)
  const usedPaths = new Set(output.definition.fields.map((field) => field.path))
  const pathMap = new Map<string, string>()

  for (const field of mask.fields) {
    const nextPath = uniquePath(field.path, usedPaths)
    pathMap.set(field.path, nextPath)
    usedPaths.add(nextPath)
  }

  const fields = mask.fields.map((field) => ({
    ...field,
    path: pathMap.get(field.path) ?? field.path,
    ...(field.visible_when ? { visible_when: remapCondition(field.visible_when, pathMap) } : {}),
    ...(field.required_when ? { required_when: remapCondition(field.required_when, pathMap) } : {}),
  }))

  output.definition.fields.push(...fields)
  output.definition.rules ??= []
  const usedRuleIds = new Set(output.definition.rules.map((rule) => rule.id))
  const ruleIds: string[] = []
  for (const rule of mask.rules) {
    const id = uniqueId(rule.id, usedRuleIds)
    usedRuleIds.add(id)
    ruleIds.push(id)
    output.definition.rules.push({
      ...rule,
      id,
      path: pathMap.get(rule.path) ?? rule.path,
      assert: remapCondition(rule.assert, pathMap),
      ...(rule.when ? { when: remapCondition(rule.when, pathMap) } : {}),
    })
  }

  output.presentation ??= {
    default_locale: 'pl',
    sections: [{
      id: 'general',
      title: 'Formularz',
      field_paths: output.definition.fields
        .map((field) => field.path)
        .filter((path) => !fields.some((field) => field.path === path)),
    }],
    fields: {},
  }
  output.presentation.fields ??= {}
  const usedSectionIds = new Set(output.presentation.sections.map((section) => section.id))
  const sectionIds: string[] = []

  for (const section of mask.sections) {
    const id = uniqueId(section.id, usedSectionIds)
    usedSectionIds.add(id)
    sectionIds.push(id)
    output.presentation.sections.push({
      ...section,
      id,
      field_paths: section.field_paths.map((path) => pathMap.get(path) ?? path),
    })
  }

  for (const [path, presentation] of Object.entries(mask.presentation ?? {})) {
    output.presentation.fields[pathMap.get(path) ?? path] = presentation
  }

  delete output.integrity

  return {
    package: output,
    sectionIds,
    fieldPaths: fields.map((field) => field.path),
    ruleIds,
  }
}
